// Updated client/src/components/Navbar.js
import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useClubSettings } from "../contexts/ClubSettingsContext";
import { ClubSwitcher } from "./ClubSelection";
import {
  Bars3Icon,
  XMarkIcon,
  UserCircleIcon,
  ArrowRightStartOnRectangleIcon,
  Cog6ToothIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  UserGroupIcon,
  MegaphoneIcon,
  UsersIcon,
  ClipboardIcon,
  AcademicCapIcon,
  RectangleGroupIcon,
} from "@heroicons/react/24/outline";

const Navbar = () => {
  const { user, logout, switchClub } = useAuth();
  const { isSkillsBasedClub, isGroupsBasedClub } = useClubSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [switchingClub, setSwitchingClub] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/portal/login");
  };

  const handleSwitchClub = async (clubId) => {
    setSwitchingClub(true);
    try {
      const result = await switchClub(clubId);
      if (result.success) {
        // Navigate to the new club's dashboard
        navigate(`/portal/club/${clubId}/dashboard`);
      }
    } catch (error) {
      console.error("Error switching clubs:", error);
    } finally {
      setSwitchingClub(false);
    }
  };

  const getNavigationItems = () => {
    if (!user) return [];

    const baseItems = [];

    if (user.role === "super_admin") {
      // For super admin, check if they're viewing a club page
      const currentPath = location.pathname;
      const clubMatch = currentPath.match(/^\/portal\/club\/([^/]+)/);

      if (clubMatch) {
        // Super admin viewing a club - show club navigation
        const clubId = clubMatch[1];
        const clubPath = `/portal/club/${clubId}`;
        baseItems.push(
          {
            name: "Dashboard",
            href: `${clubPath}/dashboard`,
            icon: BuildingOfficeIcon,
          },
          { name: "Staff", href: `${clubPath}/staff`, icon: UsersIcon },
          {
            name: "Evaluations",
            href: `${clubPath}/evaluations`,
            icon: ClipboardIcon,
          },
        );

        // Add evaluation-type specific items for super admin
        if (isSkillsBasedClub) {
          baseItems.push({
            name: "Skills",
            href: `${clubPath}/skills`,
            icon: AcademicCapIcon,
          });
        } else if (isGroupsBasedClub) {
          baseItems.push({
            name: "Groups",
            href: `${clubPath}/groups`,
            icon: RectangleGroupIcon,
          });
        }

        baseItems.push(
          {
            name: "Invoices",
            href: `${clubPath}/invoices`,
            icon: CreditCardIcon,
          },
          {
            name: "Settings",
            href: `${clubPath}/settings`,
            icon: Cog6ToothIcon,
          },
        );
      } else {
        // Super admin on admin pages - show admin navigation
        baseItems.push(
          {
            name: "Dashboard",
            href: "/admin/dashboard",
            icon: BuildingOfficeIcon,
          },
          { name: "Billing", href: "/admin/billing", icon: CreditCardIcon },
          { name: "Clubs", href: "/admin/clubs", icon: UserGroupIcon },
          { name: "Marketing", href: "/admin/marketing", icon: MegaphoneIcon },
        );
      }
    } else if (user.club_id) {
      const clubPath = `/portal/club/${user.club_id}`;
      if (user.role !== "coordinator") {
        baseItems.push({
          name: "Dashboard",
          href: `${clubPath}/dashboard`,
          icon: BuildingOfficeIcon,
        });
      }

      baseItems.push({
        name: "Evaluations",
        href: `${clubPath}/evaluations`,
        icon: ClipboardIcon,
      });

      if (user.role === "coach") {
        // Add evaluation-type specific items for club admin
        if (isSkillsBasedClub) {
          baseItems.push({
            name: "Skills",
            href: `${clubPath}/skills`,
            icon: AcademicCapIcon,
          });
        } else if (isGroupsBasedClub) {
          baseItems.push({
            name: "Groups",
            href: `${clubPath}/groups`,
            icon: RectangleGroupIcon,
          });
        }
      }

      if (user.role === "club_admin") {
        baseItems.push({
          name: "Staff",
          href: `${clubPath}/staff`,
          icon: UsersIcon,
        });
        // Add evaluation-type specific items for club admin
        if (isSkillsBasedClub) {
          baseItems.push({
            name: "Skills",
            href: `${clubPath}/skills`,
            icon: AcademicCapIcon,
          });
        } else if (isGroupsBasedClub) {
          baseItems.push({
            name: "Groups",
            href: `${clubPath}/groups`,
            icon: RectangleGroupIcon,
          });
        }

        baseItems.push(
          {
            name: "Invoices",
            href: `${clubPath}/invoices`,
            icon: CreditCardIcon,
          },
          {
            name: "Settings",
            href: `${clubPath}/settings`,
            icon: Cog6ToothIcon,
          },
        );
      }
    }

    return baseItems;
  };
  const navigationItems = getNavigationItems();

  const isCurrentPath = (href) => {
    return (
      location.pathname === href ||
      (href !== "/" && location.pathname.startsWith(href))
    );
  };

  if (!user) {
    return null;
  }

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 justify-between">
          <div className="flex">
            {/* Logo */}
            <div className="flex flex-shrink-0 items-center">
              <Link
                to={
                  user.role === "super_admin"
                    ? "/admin/dashboard"
                    : user.role === "coordinator"
                      ? `/portal/club/${user.club_id}/evaluations`
                      : `/portal/club/${user.club_id}/dashboard`
                }
              >
                <img
                  className="h-8 w-auto"
                  src="/Logo_words.png"
                  alt="Eval Ninja"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden sm:-my-px sm:ml-6 sm:flex sm:space-x-8">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors ${
                    isCurrentPath(item.href)
                      ? "border-sky-500 text-gray-900"
                      : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                  }`}
                >
                  {item.icon && <item.icon className="w-4 h-4 mr-2" />}
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="hidden sm:ml-6 sm:flex sm:items-center sm:space-x-4">
            {/* Club Switcher - only show if user has multiple clubs */}
            {user.available_clubs && user.available_clubs.length > 1 && (
              <ClubSwitcher
                currentClub={{
                  id: user.club_id,
                  name: user.club_name,
                }}
                availableClubs={user.available_clubs}
                onSwitchClub={handleSwitchClub}
                loading={switchingClub}
              />
            )}

            {/* User menu */}
            <div className="relative">
              <button
                type="button"
                className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <span className="sr-only">Open user menu</span>
                <div className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-700">
                  <UserCircleIcon className="w-6 h-6" />
                  <span className="hidden lg:block">{user.name}</span>
                </div>
              </button>

              {userMenuOpen && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setUserMenuOpen(false)}
                  />

                  {/* Dropdown */}
                  <div className="absolute right-0 z-20 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                    <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
                      <div className="font-medium">{user.name}</div>
                      <div className="text-xs">{user.email}</div>
                      {user.club_name && (
                        <div className="text-xs mt-1 text-sky-600">
                          {user.club_name} • {user.role.replace("_", " ")}
                        </div>
                      )}
                    </div>

                    <Link
                      to="/portal/change-password"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setUserMenuOpen(false)}
                    >
                      <Cog6ToothIcon className="w-4 h-4 mr-2" />
                      Change Password
                    </Link>

                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <ArrowRightStartOnRectangleIcon className="w-4 h-4 mr-2" />
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="-mr-2 flex items-center sm:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md bg-white p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
              onClick={() => setIsOpen(!isOpen)}
            >
              <span className="sr-only">Open main menu</span>
              {isOpen ? (
                <XMarkIcon className="block h-6 w-6" />
              ) : (
                <Bars3Icon className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="sm:hidden">
          <div className="space-y-1 pb-3 pt-2">
            {/* Club Switcher for mobile */}
            {user.available_clubs && user.available_clubs.length > 1 && (
              <div className="px-4 py-2 border-b border-gray-100">
                <ClubSwitcher
                  currentClub={{
                    id: user.club_id,
                    name: user.club_name,
                  }}
                  availableClubs={user.available_clubs}
                  onSwitchClub={handleSwitchClub}
                  loading={switchingClub}
                />
              </div>
            )}

            {/* Navigation items */}
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`block border-l-4 py-2 pl-3 pr-4 text-base font-medium transition-colors ${
                  isCurrentPath(item.href)
                    ? "border-sky-500 bg-sky-50 text-sky-700"
                    : "border-transparent text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800"
                }`}
                onClick={() => setIsOpen(false)}
              >
                <div className="flex items-center">
                  {item.icon && <item.icon className="w-4 h-4 mr-2" />}
                  {item.name}
                </div>
              </Link>
            ))}
          </div>

          {/* Mobile user menu */}
          <div className="border-t border-gray-200 pb-3 pt-4">
            <div className="flex items-center px-4">
              <div className="flex-shrink-0">
                <UserCircleIcon className="h-8 w-8 text-gray-400" />
              </div>
              <div className="ml-3">
                <div className="text-base font-medium text-gray-800">
                  {user.name}
                </div>
                <div className="text-sm text-gray-500">{user.email}</div>
                {user.club_name && (
                  <div className="text-sm text-sky-600">
                    {user.club_name} • {user.role.replace("_", " ")}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3 space-y-1">
              <Link
                to="/portal/change-password"
                className="flex items-center px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                onClick={() => setIsOpen(false)}
              >
                <Cog6ToothIcon className="w-4 h-4 mr-2" />
                Change Password
              </Link>
              <button
                onClick={() => {
                  setIsOpen(false);
                  handleLogout();
                }}
                className="flex w-full items-center px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              >
                <ArrowRightStartOnRectangleIcon className="w-4 h-4 mr-2" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
